<script setup>
/**
 * The Journal's plum leather cover (journal redesign 2026-10-27), gold tooled.
 * mode 'first': the cover that opens once, the first time the book is opened.
 * mode 'lock':  clasped. "Open with Face ID" (a passkey), "Use PIN", and
 * "Forgot your PIN?" → a sign-in code by email → a new PIN. The book animates
 * the cover, strap and clasp through the exposed refs.
 */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import JournalGlyph from './JournalGlyph.vue'

const props = defineProps({
  name: { type: String, default: '' },
  mode: { type: String, default: 'first' },      // 'first' | 'lock'
  step: { type: String, default: 'cover' },      // 'cover' | 'pin' | 'forgot' | 'code' | 'newpin'
  entry: { type: String, default: '' },
  err: { type: String, default: '' },
  email: { type: String, default: '' },
  bio: { type: String, default: '' },            // 'Face ID' … or '' when no passkey here
  busy: { type: Boolean, default: false },
  shake: { type: Number, default: 0 },
  shine: { type: Boolean, default: false },
  reduced: { type: Boolean, default: false },
})
const emit = defineEmits(['bio', 'step', 'digit', 'send-code'])

const coverEl = ref(null)
const strapEl = ref(null)
const claspEl = ref(null)
defineExpose({ coverEl, strapEl, claspEl })

const n = computed(() => (props.step === 'code' ? 6 : 4))
const hint = computed(() => ({ pin: 'Enter your PIN', code: `Enter the code sent to ${props.email || 'your email'}`, newpin: 'Choose a new PIN' }[props.step] || ''))
const compact = computed(() => props.mode === 'lock' && props.step !== 'cover')
const padding = computed(() => ['pin', 'code', 'newpin'].includes(props.step))
const bioGlyph = computed(() => (/Touch|fingerprint/i.test(props.bio) ? 'finger' : 'face'))

const dotsEl = ref(null)
watch(() => props.shake, () => {
  if (props.reduced || !dotsEl.value || !dotsEl.value.animate) return
  dotsEl.value.animate([{ transform: 'none' }, { transform: 'translateX(-7px)' }, { transform: 'translateX(7px)' }, { transform: 'translateX(-7px)' }, { transform: 'translateX(7px)' }, { transform: 'none' }], { duration: 400 })
})

function onKey (e) {
  if (!padding.value || props.busy || !coverEl.value || coverEl.value.offsetParent === null) return
  if (/INPUT|TEXTAREA/.test((document.activeElement && document.activeElement.tagName) || '')) return
  if (/^[0-9]$/.test(e.key)) { emit('digit', e.key); e.preventDefault() }
  else if (e.key === 'Backspace') { emit('digit', 'del'); e.preventDefault() }
}
onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => window.removeEventListener('keydown', onKey))
</script>

<template>
  <div ref="coverEl" class="cover" :data-mode="mode" data-testid="journal-cover">
    <div class="tool"><i v-for="c in ['tl', 'tr', 'bl', 'br']" :key="c" :class="c"><JournalGlyph name="star" /></i></div>
    <div class="face">
      <template v-if="!compact">
        <JournalGlyph name="moon" class="emb" />
        <div class="ct">The journal of</div>
        <div class="cn" :class="{ shine: shine && !reduced }">{{ name || 'You' }}</div>
      </template>
      <div v-if="mode === 'lock'" class="ui">
        <template v-if="step === 'cover'">
          <button v-if="bio" type="button" class="gbtn" :disabled="busy" data-testid="journal-bio" @click="emit('bio')"><JournalGlyph :name="bioGlyph" />Open with {{ bio }}</button>
          <button v-else type="button" class="gbtn" data-testid="journal-use-pin" @click="emit('step', 'pin')"><JournalGlyph name="lock" />Open with your PIN</button>
          <button v-if="bio" type="button" class="glink" data-testid="journal-use-pin" @click="emit('step', 'pin')">Use PIN</button>
          <p v-if="err" class="err" role="alert">{{ err }}</p>
        </template>
        <template v-else-if="padding">
          <p class="hint">{{ hint }}</p>
          <div ref="dotsEl" class="dots" :aria-label="`${entry.length} of ${n} digits`" data-testid="journal-pin-dots"><i v-for="i in n" :key="i" :class="{ f: i <= entry.length }"></i></div>
          <p class="err" role="alert">{{ err }}</p>
          <div class="pad">
            <button v-for="d in [1, 2, 3, 4, 5, 6, 7, 8, 9]" :key="d" type="button" :disabled="busy" :data-testid="`journal-pin-${d}`" @click="emit('digit', String(d))">{{ d }}</button>
            <span></span>
            <button type="button" :disabled="busy" data-testid="journal-pin-0" @click="emit('digit', '0')">0</button>
            <button type="button" class="sm" :disabled="busy" aria-label="Delete a digit" @click="emit('digit', 'del')">⌫</button>
          </div>
          <button v-if="step === 'pin'" type="button" class="glink" data-testid="journal-forgot" @click="emit('step', 'forgot')">Forgot your PIN?</button>
          <button v-else type="button" class="glink" @click="emit('step', 'cover')">Back</button>
        </template>
        <template v-else-if="step === 'forgot'">
          <p class="hint">We’ll email a sign-in code to {{ email || 'the address on your account' }}. It opens your journal, then you choose a new PIN.</p>
          <button type="button" class="gbtn" :disabled="busy" data-testid="journal-send-code" @click="emit('send-code')">Email me a code</button>
          <p v-if="err" class="err" role="alert">{{ err }}</p>
          <button type="button" class="glink" @click="emit('step', 'pin')">Back</button>
        </template>
      </div>
    </div>
    <div v-if="mode === 'lock'" ref="strapEl" class="strap"><span ref="claspEl" class="clasp"><JournalGlyph name="star" /></span></div>
  </div>
</template>

<style scoped>
.cover { position: absolute; inset: 0; z-index: 12; transform-origin: left center; backface-visibility: hidden; -webkit-backface-visibility: hidden; border-radius: 4px 12px 12px 4px;
  background: radial-gradient(120% 80% at 30% 20%, rgba(255, 255, 255, .07), transparent 60%),
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.4' numOctaves='3'/%3E%3CfeColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 .22 0'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E"),
    linear-gradient(160deg, #4a2672, var(--j-leather) 45%, var(--j-leather2));
  box-shadow: inset 6px 0 10px rgba(0, 0, 0, .35), inset 0 0 0 1px rgba(255, 223, 163, .12), 0 16px 40px rgba(0, 0, 0, .55); color: #ffdfa3; overflow: hidden; }
.tool { position: absolute; inset: 16px 16px 16px 22px; border: 1.5px solid rgba(201, 169, 78, .6); border-radius: 6px; pointer-events: none; }
.tool::before { content: ''; position: absolute; inset: 5px; border: 1px solid rgba(201, 169, 78, .35); border-radius: 4px; }
.tool i { position: absolute; width: 10px; height: 10px; color: #c9a94e; display: block; }
.tool i :deep(svg) { width: 10px; height: 10px; display: block; }
.tool .tl { left: -5px; top: -5px; } .tool .tr { right: -5px; top: -5px; } .tool .bl { left: -5px; bottom: -5px; } .tool .br { right: -5px; bottom: -5px; }
.face { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 34px 40px 40px; text-align: center; }
.emb { width: 84px; height: 84px; color: #c9a94e; margin-bottom: 14px; }
.ct { font: 600 13px var(--j-display); letter-spacing: .3em; text-transform: uppercase; color: #c9a94e; }
.cn { font: italic 600 38px/1.1 var(--j-serif); margin-top: 6px; background: linear-gradient(100deg, #c9a94e 20%, #fff1c8 40%, #c9a94e 60%); background-size: 300% 100%; background-position: 100% 0; -webkit-background-clip: text; background-clip: text; color: transparent; }
.cn.shine { animation: shine 1.6s .2s ease-in-out; }
@keyframes shine { from { background-position: 100% 0; } to { background-position: 0 0; } }
.ui { margin-top: 24px; display: flex; flex-direction: column; align-items: center; gap: 12px; min-height: 90px; }
.gbtn { display: inline-flex; align-items: center; gap: 8px; padding: 12px 22px; min-height: 48px; border-radius: 999px; background: linear-gradient(180deg, #f3d58f, #d9b25e); color: #2a1845; font: 700 17px var(--j-serif); box-shadow: 0 4px 14px rgba(0, 0, 0, .35); border: 0; cursor: pointer; }
.gbtn :deep(svg) { width: 20px; height: 20px; }
.gbtn:disabled { opacity: .7; cursor: default; }
.glink { font: 600 13px var(--j-ui); color: #ffdfa3; opacity: .85; text-decoration: underline; text-underline-offset: 3px; background: none; border: 0; padding: 10px 6px; cursor: pointer; }
.hint { margin: 0; font: 500 13px/1.4 var(--j-ui); color: rgba(255, 223, 163, .75); max-width: 240px; }
.dots { display: flex; gap: 12px; justify-content: center; margin: 2px 0 4px; }
.dots i { width: 12px; height: 12px; border-radius: 50%; border: 1.5px solid currentColor; display: block; box-sizing: border-box; }
.dots i.f { background: currentColor; }
.pad { display: grid; grid-template-columns: repeat(3, 58px); gap: 8px 18px; justify-content: center; }
.pad button { height: 48px; border-radius: 50%; font: 300 23px var(--j-ui); color: #ffdfa3; background: none; border: 0; cursor: pointer; }
.pad button:hover, .pad button:focus-visible { background: rgba(255, 223, 163, .08); }
.pad button.sm { font: 600 13px var(--j-ui); }
.err { margin: 0; font: 500 13px var(--j-ui); color: #ffc9b8; min-height: 18px; max-width: 260px; }
.strap { position: absolute; right: -2px; top: 50%; margin-top: -26px; width: 74px; height: 52px; z-index: 3; border-radius: 10px 0 0 10px;
  background: linear-gradient(180deg, #5a2f86, #3a1d5c 60%, #2a1345); box-shadow: -3px 2px 8px rgba(0, 0, 0, .45), inset 0 1px 0 rgba(255, 255, 255, .08); }
.strap::before { content: ''; position: absolute; inset: 4px 0 4px 4px; border: 1px dashed rgba(201, 169, 78, .45); border-right: 0; border-radius: 7px 0 0 7px; }
.clasp { position: absolute; left: 8px; top: 50%; width: 36px; height: 36px; margin-top: -18px; border-radius: 50%; background: radial-gradient(circle at 35% 30%, #fff1c8, #d9b25e 45%, #8c6628);
  box-shadow: 0 2px 5px rgba(0, 0, 0, .5), inset 0 -2px 3px rgba(0, 0, 0, .25); display: flex; align-items: center; justify-content: center; color: #5a3c10; transform-origin: 70% 50%; }
.clasp :deep(svg) { width: 18px; height: 18px; }
</style>
